import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { hasAnalyticsConsent } from "@/lib/googleTag";
import { getAnalyticsSessionId } from "@/lib/sessionId";

/**
 * Records a single page view per article per session per hour.
 * Article id should be a real DB UUID; static-fallback articles are skipped.
 *
 * Privacy: persistence is gated on analytics consent. Without consent we
 * neither write to localStorage nor insert into the database — symmetric with
 * Google Consent Mode in `useArticleAnalytics`.
 */
export function useTrackView(articleId: string | undefined) {
  useEffect(() => {
    if (!articleId) return;
    // Skip static-fallback string ids — only real DB UUIDs.
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        articleId,
      );
    if (!isUuid) return;
    // Privacy gate — no DB write without analytics consent.
    if (!hasAnalyticsConsent()) return;

    const sessionId = getAnalyticsSessionId();
    const dedupeKey = `view_${articleId}_${new Date().toISOString().slice(0, 13)}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, "1");

    supabase.auth.getUser().then(({ data }) => {
      supabase
        .from("article_views")
        .insert({
          article_id: articleId,
          user_id: data.user?.id || null,
          session_id: sessionId,
        })
        .then(({ error }) => {
          if (error)
            console.warn(
              "[useTrackView] failed to record view:",
              error.message,
            );
        });
    });
  }, [articleId]);
}
